import reducerRegistry from '@comeon/reducer-registry';
import { requestApi, parseResponse } from '~/services/api.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { currentGameSelector, gameModeSelector } from '~/components/casino-shared/game-loader/ducks/selectors.js';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectEnableInhouseJackpot } from '~/components/brand/ducks/selectors';
import currencyFormatter from '~/libs/currency-formatter.js';
import { createSelector } from 'reselect';

const REDUCER_NAME = 'inhouseJackpot';
const baseUrl = '/rest/casino/inhouse-jackpot/';

const ACTION_PREFIX = '@komigen/mobile-frontend/inhouse-jackpot';
export const FETCH_OPTEDIN_JACKPOTS_LOADING = `${ACTION_PREFIX}/FETCH_OPTEDIN_JACKPOTS_LOADING`;
export const OPTIN_LOADING = `${ACTION_PREFIX}/OPTIN_LOADING`;
export const OPTOUT_LOADING = `${ACTION_PREFIX}/OPTOUT_LOADING`;
export const SET_OPTEDIN_JACKPOTS = `${ACTION_PREFIX}/SET_OPTEDIN_JACKPOTS`;
export const SET_CURRENT_GAMES_OPTEDIN_JACKPOT = `${ACTION_PREFIX}/SET_CURRENT_GAMES_OPTEDIN_JACKPOT`;
export const SET_IS_MODAL_SHOWN = `${ACTION_PREFIX}/SET_IS_MODAL_SHOWN`;
export const SET_JACKPOT_DETAILS = `${ACTION_PREFIX}/SET_JACKPOT_DETAILS`;
export const SET_JACKPOT_TIERS = `${ACTION_PREFIX}/SET_JACKPOT_TIERS`;
export const UPDATE_JACKPOT_TIERS = `${ACTION_PREFIX}/UPDATE_JACKPOT_TIERS`;
export const JACKPOT_DETAILS_LOADING = `${ACTION_PREFIX}/JACKPOT_DETAILS_LOADING`;
export const SET_IS_WIN_ANIMATION_SHOWN = `${ACTION_PREFIX}/SET_IS_WIN_ANIMATION_SHOWN`;
export const SET_WIN_ANIMATION_DATA = `${ACTION_PREFIX}/SET_WIN_ANIMATION_DATA`;
export const INVALIDATE_INHOUSE_JACKPOT_STATE = `${ACTION_PREFIX}/INVALIDATE_INHOUSE_JACKPOT_STATE`;
export const JACKPOT_GAME_CLOSED = `${ACTION_PREFIX}/JACKPOT_GAME_CLOSED`;
export const JACKPOT_GAME_STARTED = `${ACTION_PREFIX}/JACKPOT_GAME_STARTED`;

const initialState = {
	optedinInhouseJackpots: [],
	currentGamesOptedinJackpot: null,
	fetchOptedinJackpotsLoading: false,
	jackpotDetailsLoading: false,
	optinLoading: false,
	optoutLoading: false,
	isModalShown: false,
	jackpotDetails: null,
	jackpotTiers: null,
	isWinAnimationShown: null,
	winAnimationData: null,
	isInGame: false
};

const showGeneralError = dispatch => {
	dispatch(
		showNotification({
			color: 'red',
			autoHide: true,
			messageKey: 'INHOUSE_JACKPOT_GENERAL_REQUEST_ERROR'
		})
	);
};

// methods START
const sendRequest = async (dispatch, url, responseActionType, loadingActionType, method = 'GET', payload = {}) => {
	dispatch({
		type: loadingActionType,
		payload: true
	});
	try {
		let response = await requestApi(baseUrl + url, payload, method);
		if (response.status === 'FAILURE') {
			dispatch(
				showNotification({
					color: 'red',
					autoHide: true,
					message: response.result
				})
			);
			return;
		}
		response = parseResponse(response);
		dispatch({
			type: responseActionType,
			payload: response
		});
		return response;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);
		dispatch(
			showNotification({
				color: 'red',
				autoHide: true,
				messageKey: 'INHOUSE_JACKPOT_GENERAL_REQUEST_ERROR'
			})
		);
	} finally {
		dispatch({
			type: loadingActionType,
			payload: false
		});
	}
};
const findOptedinInhouseJackpotForCurrentGame = (inhouseJackpotsOfCurrentGame = [], optedInJackpots = []) => {
	try {
		return inhouseJackpotsOfCurrentGame.filter(el => {
			return optedInJackpots.includes(el.id);
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);
		return [];
	}
};

const addFormattedTierAmounts = (tiers, currency) => {
	return tiers.map(tier => {
		return {
			...tier,
			formattedTierValue: currencyFormatter.format(tier.value, currency, '', true)
		};
	});
};
// methods END

// selectors START
export const optedinInhouseJackpotsSelector = state => state.inhouseJackpot?.optedinInhouseJackpots;
export const currentGamesOptedinJackpotSelector = state => state.inhouseJackpot?.currentGamesOptedinJackpot;
export const fetchOptedinJackpotsLoadingSelector = state => state.inhouseJackpot?.fetchOptedinJackpotsLoading;
export const jackpotDetailsLoadingSelector = state => state.inhouseJackpot?.jackpotDetailsLoading;
export const optinLoadingSelector = state => state.inhouseJackpot?.optinLoading;
export const optoutLoadingSelector = state => state.inhouseJackpot?.optoutLoading;
export const isModalShownSelector = state => state.inhouseJackpot?.isModalShown;
export const jackpotDetailsSelector = state => state.inhouseJackpot?.jackpotDetails;
export const jackpotTiersSelector = state => state.inhouseJackpot?.jackpotTiers;
export const isWinAnimationShownSelector = state => state.inhouseJackpot?.isWinAnimationShown;
export const winAnimationDataSelector = state => state.inhouseJackpot?.winAnimationData;
export const isWinModalEnabledSelector = state => !state.inhouseJackpot?.isInGame;
export const inhouseJackpotAvailableInCurrentGameSelector = createSelector(
	currentGameSelector,
	gameModeSelector,
	authenticationSelector,
	selectEnableInhouseJackpot,
	(currentGame, currentGameMode, authenticated, isInhouseJackpotEnabled) => {
		return (
			authenticated &&
			isInhouseJackpotEnabled &&
			!!currentGame?.inhouseJackpots?.length &&
			currentGameMode === 'real'
		);
	}
);
// selectors END

// action creators START
export const setIsModalShown = payload => {
	return {
		type: SET_IS_MODAL_SHOWN,
		payload
	};
};
export const setIsWinAnimationShown = payload => {
	return {
		type: SET_IS_WIN_ANIMATION_SHOWN,
		payload
	};
};
export const setWinAnimationData = payload => {
	return {
		type: SET_WIN_ANIMATION_DATA,
		payload
	};
};
export const invalidateInhouseJackpotState = () => {
	return { type: INVALIDATE_INHOUSE_JACKPOT_STATE };
};
export const setFetchOptedinJackpotsLoading = payload => ({
	type: FETCH_OPTEDIN_JACKPOTS_LOADING,
	payload
});
export const enableJackpotPopup = () => {
	return {
		type: JACKPOT_GAME_CLOSED
	};
};
export const disableJackpotPopup = () => {
	return {
		type: JACKPOT_GAME_STARTED
	};
};
export const updateJackpotTiers = payload => {
	return {
		type: UPDATE_JACKPOT_TIERS,
		payload
	};
};
// action creators END

// thunks START
const setCurrentGamesOptedinJackpot = () => (dispatch, getState) => {
	const state = getState();
	const currentGame = currentGameSelector(state);
	const optedinInhouseJackpots = optedinInhouseJackpotsSelector(state);
	const currentGamesOptedinJackpots = findOptedinInhouseJackpotForCurrentGame(
		currentGame?.inhouseJackpots,
		optedinInhouseJackpots
	);
	dispatch({
		type: SET_CURRENT_GAMES_OPTEDIN_JACKPOT,
		payload: currentGamesOptedinJackpots?.[0] || null // for now, we will consume just first jackpot of the current game, this may be change in future
	});
};
export const fetchOptedInJackpots = () => {
	return async dispatch => {
		await sendRequest(dispatch, 'optin-jackpots', SET_OPTEDIN_JACKPOTS, FETCH_OPTEDIN_JACKPOTS_LOADING);
		dispatch(setCurrentGamesOptedinJackpot());
	};
};
export const optIn = jackpotId => {
	return async dispatch => {
		if (!jackpotId) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided');
			showGeneralError(dispatch);
			return;
		}
		await sendRequest(dispatch, `optin/${jackpotId}`, SET_OPTEDIN_JACKPOTS, OPTIN_LOADING, 'PUT');
		await dispatch(fetchOptedInJackpots());
	};
};
export const optOut = jackpotId => {
	return async dispatch => {
		if (!jackpotId) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided');
			showGeneralError(dispatch);
			return;
		}
		await sendRequest(dispatch, `optout/${jackpotId}`, SET_OPTEDIN_JACKPOTS, OPTOUT_LOADING, 'PUT');
		await dispatch(fetchOptedInJackpots());
	};
};
export const fetchJackpotDetails = jackpotId => {
	return async (dispatch, getState) => {
		if (!jackpotId) {
			// eslint-disable-next-line no-console
			console.error('No jackpot id provided');
			showGeneralError(dispatch);
			return;
		}
		const response = await sendRequest(
			dispatch,
			`detail/${jackpotId}`,
			SET_JACKPOT_DETAILS,
			JACKPOT_DETAILS_LOADING
		);

		const state = getState();
		const jackpotTiers = jackpotTiersSelector(state);

		// if tier data not filled yet from web socket, fill it from the fetch response above
		if (!jackpotTiers || !jackpotTiers?.length) {
			dispatch({
				type: SET_JACKPOT_TIERS,
				payload: response?.tiers
			});
		}
	};
};
// thunks END

// reducers START
function reducer(state = initialState, { type, payload }) {
	switch (type) {
		case SET_OPTEDIN_JACKPOTS:
			return { ...state, optedinInhouseJackpots: payload?.jackpots };
		case OPTIN_LOADING:
			return { ...state, optinLoading: payload };
		case OPTOUT_LOADING:
			return { ...state, optoutLoading: payload };
		case FETCH_OPTEDIN_JACKPOTS_LOADING:
			return { ...state, fetchOptedinJackpotsLoading: payload };
		case SET_CURRENT_GAMES_OPTEDIN_JACKPOT:
			return { ...state, currentGamesOptedinJackpot: payload };
		case SET_IS_MODAL_SHOWN:
			return { ...state, isModalShown: payload };
		case JACKPOT_DETAILS_LOADING:
			return { ...state, jackpotDetailsLoading: payload };
		case SET_JACKPOT_DETAILS:
			return { ...state, jackpotDetails: payload };
		case SET_JACKPOT_TIERS: {
			let tiers = JSON.parse(JSON.stringify(payload));

			// ggr is coming from service and we don't need to show it
			tiers = tiers.filter(item => {
				return item.id !== 'ggr';
			});

			return { ...state, jackpotTiers: addFormattedTierAmounts(tiers, state.jackpotDetails?.currency) };
		}
		case UPDATE_JACKPOT_TIERS: {
			const newTiers = {};

			// create an object with tier name and amount
			payload.forEach(tier => {
				newTiers[tier.name] = tier.amount;
			});

			// to prevent unnecessary console error logs if there is an issue with web socket messages
			if (!state.jackpotTiers) {
				return state;
			}

			// create a new updated tiers array
			const updatedTiers = state.jackpotTiers.map(tier => {
				return {
					...tier,
					value: newTiers[tier.id]
				};
			});

			return {
				...state,
				jackpotTiers: addFormattedTierAmounts(updatedTiers, state.jackpotDetails?.currency)
			};
		}
		case SET_IS_WIN_ANIMATION_SHOWN:
			return { ...state, isWinAnimationShown: payload };
		case SET_WIN_ANIMATION_DATA:
			return { ...state, winAnimationData: payload };
		case INVALIDATE_INHOUSE_JACKPOT_STATE:
			return initialState;
		case JACKPOT_GAME_CLOSED:
			return { ...state, isInGame: false };
		case JACKPOT_GAME_STARTED:
			return { ...state, isInGame: true };
		default:
			return state;
	}
}
// reducers END

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
